import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, PencilIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F9FAFB', navy:'#0D1B2A' };

const SPECIALIZATIONS = ['IELTS Prep','Business English','Conversation','Grammar & Writing','Vocabulary','Reading & Writing','Kids / Young Learners','Exam Preparation','General English'];
const LANGUAGES = ['English','Russian','Uzbek','Kazakh','Tajik','Turkish','Arabic','French','German','Spanish','Chinese','Other'];
const NATIONALITIES = ['British','American','Uzbek','Russian','Kazakh','Tajik','Kyrgyz','Azerbaijani','Turkish','Other'];

function Field({ label, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = { width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', background:'#fff', color:C.dark };
const textareaStyle = { ...inputStyle, resize:'vertical', minHeight:100, fontFamily:'inherit' };

export default function TutorProfile() {
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    api.get('/api/tutor/profile').then(r => {
      const t = r.data.tutor;
      setTutor(t);
      setForm({
        first_name: t.first_name || '',
        last_name: t.last_name || '',
        phone: t.phone || '',
        bio: t.bio || '',
        native_language: t.native_language || '',
        nationality: t.nationality || '',
        specializations: t.specializations || [],
        profile_photo_url: t.profile_photo_url || '',
        is_online_capable: t.is_online_capable || false,
        online_platform: t.online_platform || 'zoom',
        online_link: t.online_link || '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', 'photo');
    try {
      const r = await api.post('/api/upload/tutor', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, profile_photo_url: r.data.url }));
      toast.success('Photo uploaded!');
    } catch { toast.error('Upload failed'); }
    setUploadingPhoto(false);
  };

  const toggleSpec = (spec) => {
    setForm(f => ({
      ...f,
      specializations: f.specializations.includes(spec)
        ? f.specializations.filter(s => s !== spec)
        : [...f.specializations, spec]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/api/tutor/profile', form);
      setTutor(r.data.tutor);
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <Layout role="tutor"><div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading...</div></Layout>;
  if (!tutor) return <Layout role="tutor"><div style={{ textAlign:'center', padding:60, color:C.grey }}>Profile not found</div></Layout>;

  const photoUrl = (editing ? form.profile_photo_url : tutor.profile_photo_url) || tutor.photo_url;
  const initials = `${tutor.first_name?.[0]||''}${tutor.last_name?.[0]||''}`.toUpperCase();

  return (
    <Layout role="tutor">
      <div style={{ maxWidth:700 }}>
        {/* Header card */}
        <div className="card" style={{ padding:32, marginBottom:24, display:'flex', alignItems:'center', gap:24 }}>
          <div style={{ position:'relative', flexShrink:0 }}>
            <div style={{ width:88, height:88, borderRadius:'50%', overflow:'hidden', border:`3px solid ${C.teal}` }}>
              {photoUrl
                ? <img src={photoUrl} alt={tutor.first_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <div style={{ width:'100%', height:'100%', background:`${C.teal}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:800, color:C.teal }}>{initials}</div>
              }
            </div>
            {editing && (
              <label style={{ position:'absolute', bottom:0, right:0, width:28, height:28, borderRadius:'50%', background:C.teal, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', border:'2px solid #fff' }}>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display:'none' }} />
                <PencilIcon style={{ width:12, height:12, color:'#fff' }} />
              </label>
            )}
          </div>
          <div style={{ flex:1 }}>
            <h2 style={{ margin:'0 0 6px', fontSize:20, fontWeight:800, color:C.navy }}>
              {tutor.first_name} {tutor.last_name}
            </h2>
            <div style={{ fontSize:14, color:C.grey, marginBottom:8 }}>{tutor.email}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, fontWeight:600, padding:'4px 10px', borderRadius:20,
                background: tutor.is_approved ? '#D1FAE5' : '#FEF3C7',
                color: tutor.is_approved ? '#065F46' : '#92400E' }}>
                {tutor.is_approved ? '✓ Approved' : '⏳ Pending Approval'}
              </span>
              {tutor.tutor_id_number && (
                <span style={{ fontSize:12, fontWeight:600, padding:'4px 10px', borderRadius:20, background:'#EFF6FF', color:'#1D4ED8' }}>
                  ID: {tutor.tutor_id_number}
                </span>
              )}
            </div>
          </div>
          <button onClick={() => editing ? handleSave() : setEditing(true)}
            disabled={saving}
            style={{ padding:'10px 20px', background: editing ? C.success : C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer', opacity: saving ? 0.7 : 1 }}>
            {editing ? (saving ? 'Saving...' : '✓ Save Changes') : '✎ Edit Profile'}
          </button>
          {editing && (
            <button onClick={() => setEditing(false)} style={{ padding:'10px 20px', background:'#F3F4F6', color:C.dark, border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>
              Cancel
            </button>
          )}
        </div>

        {/* Form */}
        <div className="card" style={{ padding:28 }}>
          <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Personal Information</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16, marginBottom:16 }}>
            <Field label="First Name">
              {editing
                ? <input style={inputStyle} value={form.first_name} onChange={e => setForm(f=>({...f,first_name:e.target.value}))} />
                : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, fontWeight:500 }}>{tutor.first_name || '—'}</div>}
            </Field>
            <Field label="Last Name">
              {editing
                ? <input style={inputStyle} value={form.last_name} onChange={e => setForm(f=>({...f,last_name:e.target.value}))} />
                : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, fontWeight:500 }}>{tutor.last_name || '—'}</div>}
            </Field>
            <Field label="Phone">
              {editing
                ? <input style={inputStyle} value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} />
                : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, fontWeight:500 }}>{tutor.phone || '—'}</div>}
            </Field>
            <Field label="Native Language">
              {editing
                ? <select style={inputStyle} value={form.native_language} onChange={e => setForm(f=>({...f,native_language:e.target.value}))}>
                    <option value="">Select...</option>
                    {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, fontWeight:500 }}>{tutor.native_language || '—'}</div>}
            </Field>
            <Field label="Nationality">
              {editing
                ? <select style={inputStyle} value={form.nationality} onChange={e => setForm(f=>({...f,nationality:e.target.value}))}>
                    <option value="">Select...</option>
                    {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, fontWeight:500 }}>{tutor.nationality || '—'}</div>}
            </Field>
          </div>

          <Field label="About Me / Bio">
            {editing
              ? <textarea style={textareaStyle} value={form.bio} onChange={e => setForm(f=>({...f,bio:e.target.value}))} placeholder="Tell students about yourself..." />
              : <div style={{ padding:'10px 0', fontSize:14, color:C.dark, lineHeight:1.6 }}>{tutor.bio || <span style={{color:C.grey}}>No bio added yet</span>}</div>}
          </Field>

          {/* ── Online Teaching ── */}
          <div style={{ marginTop:24, paddingTop:20, borderTop:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <span style={{ fontSize:20 }}>🖥️</span>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:C.dark }}>Online Teaching</div>
                <div style={{ fontSize:12, color:C.grey }}>For remote students who attend via video call</div>
              </div>
            </div>
            {/* Video room is assigned by admin — read-only for tutors */}
            {tutor.is_online_capable && tutor.online_link ? (
              <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                  <span style={{ background:'#D1FAE5', color:'#065F46', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>✓ Online Teaching Enabled</span>
                  {tutor.online_platform && (
                    <span style={{ background:'#EFF6FF', color:'#1D4ED8', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>
                      {tutor.online_platform === 'zoom' ? '🎥 Zoom' : tutor.online_platform === 'google_meet' ? '📹 Google Meet' : tutor.online_platform === 'teams' ? '💼 Teams' : '📞 Skype'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:12, color:'#374151', marginBottom:4 }}>Your video room (assigned by school):</div>
                <a href={tutor.online_link} target='_blank' rel='noreferrer'
                  style={{ fontSize:13, color:'#0D9488', fontWeight:600, textDecoration:'none', wordBreak:'break-all' }}>
                  🔗 {tutor.online_link}
                </a>
                <div style={{ fontSize:11, color:'#6B7280', marginTop:6 }}>
                  This link is managed by your school. Students use it to join your sessions. Contact your school administrator to change it.
                </div>
              </div>
            ) : (
              <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:13, color:'#6B7280', marginBottom:4 }}>No video room assigned yet</div>
                <div style={{ fontSize:12, color:'#9CA3AF' }}>
                  Your school administrator will assign a Google Meet link to you. Once assigned, it will appear here and students will be able to join your online sessions.
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop:8 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:10 }}>Specializations</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {(editing ? SPECIALIZATIONS : (tutor.specializations?.length ? tutor.specializations : SPECIALIZATIONS)).map(spec => {
                const active = editing ? form.specializations.includes(spec) : (tutor.specializations||[]).includes(spec);
                if (!editing && !active) return null;
                return (
                  <div key={spec} onClick={() => editing && toggleSpec(spec)} style={{
                    padding:'6px 14px', borderRadius:20, fontSize:13, fontWeight:600,
                    background: active ? `${C.teal}20` : '#F3F4F6',
                    color: active ? C.teal : C.grey,
                    border: `1.5px solid ${active ? C.teal : C.border}`,
                    cursor: editing ? 'pointer' : 'default',
                    userSelect:'none',
                  }}>
                    {active && editing && '✓ '}{spec}
                  </div>
                );
              })}
              {!editing && !(tutor.specializations?.length) && (
                <span style={{ color:C.grey, fontSize:14 }}>No specializations added</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
